import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { feedbackAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Bug, Lightbulb, ChatCircle, Check, Clock, Trash,
  CaretLeft, CaretRight, Eye, Prohibit
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

const STATUS_CONFIG = {
  new: { label: 'New', color: '#22C55E', bg: 'bg-green-500/10 text-green-400' },
  in_progress: { label: 'In Progress', color: '#F59E0B', bg: 'bg-yellow-500/10 text-yellow-400' },
  resolved: { label: 'Resolved', color: '#22C55E', bg: 'bg-green-500/10 text-green-400' },
  dismissed: { label: 'Dismissed', color: '#666666', bg: 'bg-[#333333] text-[#999999]' },
};

const CATEGORY_CONFIG = {
  bug: { label: 'Bug', icon: Bug, color: '#EF4444' },
  feature: { label: 'Feature', icon: Lightbulb, color: '#22C55E' },
  general: { label: 'General', icon: ChatCircle, color: '#22C55E' },
};

export default function AdminFeedbackPage() {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [adminNote, setAdminNote] = useState('');

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const { data } = await feedbackAPI.getAll(params);
      setFeedback(data.feedback);
      setTotalPages(data.pages);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  }, [page, statusFilter, categoryFilter]);

  const loadStats = useCallback(async () => {
    try { const { data } = await feedbackAPI.getStats(); setStats(data); } catch (err) { console.error('Failed to load feedback stats:', err); }
  }, []);

  useEffect(() => { loadFeedback(); loadStats(); }, [loadFeedback, loadStats]);

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await feedbackAPI.updateStatus(feedbackId, newStatus, adminNote || undefined);
      toast.success(`Marked as ${newStatus.replace('_', ' ')}`);
      setAdminNote('');
      loadFeedback();
      loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Delete this feedback permanently?')) return;
    try {
      await feedbackAPI.remove(feedbackId);
      toast.success('Feedback deleted');
      loadFeedback();
      loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <Card className="bg-[#1A1A1A] border-[#333333] max-w-md"><CardContent className="p-8 text-center"><p className="text-white text-lg font-bold mb-2">Access Denied</p><p className="text-[#999999]">Admin privileges required.</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111]" data-testid="admin-feedback-page">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" /></Link>
            <span className="text-[#666666] hidden sm:inline">/</span>
            <span className="text-white font-semibold text-sm hidden sm:inline">User Feedback</span>
          </div>
          <Link to="/dashboard">
            <Button variant="ghost" className="text-[#999999] hover:text-white hover:bg-[#333333] rounded-sm text-xs sm:text-sm">
              <ArrowLeft className="w-4 h-4 mr-1" /><span className="hidden sm:inline">Dashboard</span>
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'New', value: stats.new, color: '#22C55E' },
              { label: 'In Progress', value: stats.in_progress, color: '#F59E0B' },
              { label: 'Resolved', value: stats.resolved, color: '#22C55E' },
              { label: 'Total', value: stats.total, color: '#999999' },
            ].map(s => (
              <Card key={s.label} className="bg-[#1A1A1A] border-[#222222] rounded-lg">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                  <p className="text-[10px] uppercase tracking-wider text-[#666666] font-semibold mt-1">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Category breakdown */}
        {stats && (
          <div className="flex gap-4 mb-6">
            {[
              { label: 'Bugs', value: stats.bugs, icon: Bug, color: '#EF4444' },
              { label: 'Features', value: stats.features, icon: Lightbulb, color: '#F59E0B' },
              { label: 'General', value: stats.general, icon: ChatCircle, color: '#22C55E' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2 text-xs text-[#999999]">
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} weight="fill" />
                <span>{s.value} {s.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-36 bg-[#1A1A1A] border-[#333333] text-white rounded-lg h-9 text-sm" data-testid="feedback-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#333333]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
            <SelectTrigger className="w-36 bg-[#1A1A1A] border-[#333333] text-white rounded-lg h-9 text-sm" data-testid="feedback-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#333333]">
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="bug">Bug Reports</SelectItem>
              <SelectItem value="feature">Feature Requests</SelectItem>
              <SelectItem value="general">General</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Feedback List */}
        <Card className="bg-[#1A1A1A] border-[#222222] rounded-lg overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-[#666666]">Loading...</div>
            ) : feedback.length === 0 ? (
              <div className="p-8 text-center text-[#666666]">No feedback found</div>
            ) : (
              <div className="divide-y divide-[#222222]">
                {feedback.map((fb) => {
                  const catConfig = CATEGORY_CONFIG[fb.category] || CATEGORY_CONFIG.general;
                  const statusConfig = STATUS_CONFIG[fb.status] || STATUS_CONFIG.new;
                  const CatIcon = catConfig.icon;
                  const isExpanded = expandedId === fb.feedback_id;

                  return (
                    <div key={fb.feedback_id} className="px-4 py-3 hover:bg-[#151515] transition-colors" data-testid={`feedback-item-${fb.feedback_id}`}>
                      <div className="flex items-start gap-3 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : fb.feedback_id)}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${catConfig.color}15` }}>
                          <CatIcon className="w-4 h-4" style={{ color: catConfig.color }} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${statusConfig.bg}`}>{statusConfig.label}</span>
                            <span className="text-[10px] text-[#666666]">{catConfig.label}</span>
                          </div>
                          <p className="text-sm text-white leading-snug line-clamp-2">{fb.message}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#666666]">
                            <span>{fb.user_name || 'Anonymous'}</span>
                            {fb.user_email && <span>{fb.user_email}</span>}
                            <span>{formatDate(fb.created_at)}</span>
                          </div>
                        </div>
                        <Eye className="w-4 h-4 text-[#666666] shrink-0 mt-1" />
                      </div>

                      {isExpanded && (
                        <div className="mt-3 ml-11 space-y-3 animate-in slide-in-from-top-2 duration-200">
                          <div className="bg-[#111111] rounded-lg p-3">
                            <p className="text-sm text-[#cccccc] whitespace-pre-wrap">{fb.message}</p>
                            {fb.page_url && <p className="text-[10px] text-[#666666] mt-2">Page: {fb.page_url}</p>}
                            {fb.screenshot_url && (
                              <div className="mt-2"><img src={fb.screenshot_url} alt="Screenshot" className="max-w-xs rounded border border-[#333333]" /></div>
                            )}
                            {fb.admin_note && (
                              <div className="mt-2 p-2 bg-[#22C55E]/5 border border-[#22C55E]/20 rounded">
                                <p className="text-[10px] text-[#22C55E] font-semibold uppercase tracking-wider mb-1">Admin Note</p>
                                <p className="text-xs text-[#999999]">{fb.admin_note}</p>
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                              value={adminNote}
                              onChange={(e) => setAdminNote(e.target.value)}
                              placeholder="Add admin note (optional)..."
                              className="bg-[#111111] border-[#333333] text-white text-xs rounded-lg h-8 flex-1"
                              data-testid="admin-note-input"
                            />
                            <div className="flex gap-1.5">
                              {fb.status !== 'in_progress' && (
                                <Button size="sm" onClick={() => handleStatusChange(fb.feedback_id, 'in_progress')} className="bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 rounded-lg h-8 text-xs" data-testid={`mark-in-progress-${fb.feedback_id}`}>
                                  <Clock className="w-3 h-3 mr-1" />In Progress
                                </Button>
                              )}
                              {fb.status !== 'resolved' && (
                                <Button size="sm" onClick={() => handleStatusChange(fb.feedback_id, 'resolved')} className="bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg h-8 text-xs" data-testid={`mark-resolved-${fb.feedback_id}`}>
                                  <Check className="w-3 h-3 mr-1" />Resolve
                                </Button>
                              )}
                              {fb.status !== 'dismissed' && (
                                <Button size="sm" onClick={() => handleStatusChange(fb.feedback_id, 'dismissed')} variant="ghost" className="text-[#666666] hover:text-[#999999] rounded-lg h-8 text-xs" data-testid={`dismiss-${fb.feedback_id}`}>
                                  <Prohibit className="w-3 h-3 mr-1" />Dismiss
                                </Button>
                              )}
                              <Button size="sm" onClick={() => handleDelete(fb.feedback_id)} variant="ghost" className="text-[#666666] hover:text-red-400 rounded-lg h-8 text-xs" data-testid={`delete-feedback-${fb.feedback_id}`}>
                                <Trash className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#222222]">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-[#999999] hover:text-white"><CaretLeft className="h-4 w-4 mr-1" />Previous</Button>
                <span className="text-xs text-[#666666]">Page {page} of {totalPages}</span>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-[#999999] hover:text-white">Next<CaretRight className="h-4 w-4 ml-1" /></Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
